import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import { hasPermission } from '@/composables/usePermission'
import { useRouter } from '@core/utils/utils'
import i18n from '@/libs/i18n'
import { ref } from '@vue/composition-api'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import useAuditLog from '@/composables/useAuditLog'

export default function useTreatmentEdit() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { route, router } = useRouter()
  const { updateItem, getItem } = useLambda()
  const { addAuditEntry } = useAuditLog()

  const userData = store.state?.userStore?.userData
  const isSubmitting = ref(false)
  const collection = 'treatment'
  const treatmentData = ref({
    tiposDatos: [],
    terceros: {},
    encargados: {},
    transferencias: {},
    resguardos: {},
  })
  const originalTreatmentData = ref(null) // Store original data for comparison
  const query = { _id: oid(route.value.params.id), client_id: oid(userData.client.$oid) }

  const show = async () => {
    try {
      isSubmitting.value = true

      const result = await getItem({ collection: 'treatment', query })

      // Inicializar history si no existe (documentos legacy)
      if (result && !result.history) {
        result.history = []
      }

      treatmentData.value = result || {}
      // Store original data for change detection
      originalTreatmentData.value = JSON.parse(JSON.stringify(result || {}))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.treatment_fetch_error'))
    } finally {
      isSubmitting.value = false
    }
  }

  // Build payload helper
  const buildPayload = (data, status = null) => {
    const payload = {
      nombre: data.nombre,
      empresa_id: data.empresa_id ? oid(data.empresa_id) : null,
      rol: data.rol,
      departamento_id: data.departamento_id ? oid(data.departamento_id) : null,
      responsable_id: data.responsable_id ? oid(data.responsable_id) : null,
      finalidades: data.finalidades,
      legitimacion: data.legitimacion,
      mecanismoConsentimiento: data.mecanismoConsentimiento || '',
      tiposDatos: data.tiposDatos || [],
      hasSensitive: data.hasSensitive,
      hasMinors: data.hasMinors,
      hasBiometric: data.hasBiometric,
      excepcionSensible: data.excepcionSensible || '',
      sensiblesDescripcion: data.sensiblesDescripcion || '',
      periodicidad: data.periodicidad,
      origen: data.origen,
      categoriaInteresados: data.categoriaInteresados || '',
      medios: data.medios || '',
      volumen: data.volumen,
      soporte: data.soporte || '',
      formato: data.formato || '',
      ambito: data.ambito,
      plazoConservacion: data.plazoConservacion,
      terceros: {
        fuenteCedente: data.terceros?.fuenteCedente || '',
        fuenteRegularizacion: data.terceros?.fuenteRegularizacion || '',
        destinatario: data.terceros?.destinatario || '',
        destinatarioFinalidad: data.terceros?.destinatarioFinalidad || '',
        destinatarioLegitimacion: data.terceros?.destinatarioLegitimacion || '',
      },
      encargados: {
        identificacion: data.encargados?.identificacion || '',
        prestacion: data.encargados?.prestacion || '',
      },
      transferencias: {
        destinatario: data.transferencias?.destinatario || '',
        pais: data.transferencias?.pais || '',
      },
      resguardos: {
        papel: data.resguardos?.papel || '',
        sistemas: data.resguardos?.sistemas || '',
      },
      observaciones: data.observaciones || '',
    }

    // Only update status if explicitly provided
    if (status !== null) {
      payload.status = status
    }

    return payload
  }

  // Save as draft (for autosave and step navigation)
  // NO registra en audit log porque es autosave automático, no acción del usuario
  const saveDraft = async (data) => {
    try {
      const payload = buildPayload(data)
      const action = { $set: payload }

      await updateItem({ collection, query, action })

      // Actualizar originalData para próximas comparaciones
      originalTreatmentData.value = JSON.parse(JSON.stringify({ ...originalTreatmentData.value, ...payload }))
    } catch (error) {
      console.error('Error saving draft:', error)
      throw error
    }
  }

  // Final update (marks as active)
  const update = async (data) => {
    if (!hasPermission('privacy-treatment', 'edit')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }
    try {
      isSubmitting.value = true

      const payload = buildPayload(data, 'active')
      const action = { $set: payload }

      await updateItem({ collection, query, action })

      // Add audit log entry (sistema unificado)
      await addAuditEntry({
        collection,
        documentId: oid(route.value.params.id),
        originalData: originalTreatmentData.value,
        updatedData: payload,
      })

      showSuccessMessage('Tratamiento actualizado correctamente')
      router.push({ name: 'privacy-treatment-list' })
    } catch (error) {
      console.log(error)
      showErrorMessage('Error al actualizar tratamiento')
    } finally {
      isSubmitting.value = false
    }
  }

  return {
    update,
    saveDraft,
    show,
    treatmentData,
    isSubmitting,
  }
}
